import React, { useRef, useState } from "react";
import { formatDate } from "utils/format_date";
import {
  Box,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  IconButton,
  Menu,
  MenuItem,
} from "@mui/material";
import { useTheme } from "@emotion/react";
import { useNavigate } from "react-router-dom";
import NotFoundError from "Components/NotFoundError";
import {
  BorderColorOutlined,
  DeleteOutline,
  SaveAltOutlined,
} from "@mui/icons-material";
import CustomDialog from "Components/CustomDialog";
import { toast } from "react-toastify";
import { setGlobalIsLoading } from "state/global";
import DetailsPopUp from "Components/DetailsPopUp";
import { useDispatch } from "react-redux";
import ActionButton from "Components/ActionButton";
import { exportPdf } from "utils/esport_pdf";
import CommandeService from "Api/CommandeService";
import { deleteCommande, updateCommande } from "state/financials";

const CommandeTable = ({ commandes }) => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const [deleteCommandeOpen, setDeleteCommandeOpen] = useState(false);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [selectedCommande, setSelectedCommande] = useState({});
  const navigate = useNavigate();
  const stateButtonRef = useRef(null);
  const [stateMenuOpen, setStateMenuOpen] = useState(false);
  const tableHeader = [
    "N° B.c",
    "Client",
    "Total HT",
    "Date d’échéance",
    "Statut",
    "",
  ];
  const commandeStates = ["draft", "tovalidate", "validated", "late"];
  const exportFile = async () => {
    try {
      dispatch(setGlobalIsLoading({ isLoading: true }));
      await exportPdf(
        ".details-page",
        `commande ${selectedCommande.commande_number}.pdf`
      );
      dispatch(setGlobalIsLoading({ isLoading: false }));
    } catch (error) {
      dispatch(setGlobalIsLoading({ isLoading: false }));
      toast(`${error}`);
    }
  };
  const updateState = async (state) => {
    try {
      if (selectedCommande.state === "draft") {
        setStateMenuOpen(false);
        throw new Error(
          "Vous ne pouvez pas changer de statut brouillon à un autre status, vous devez d'abord accéder à la page de modification"
        );
      }
      dispatch(setGlobalIsLoading({ isLoading: true }));
      const data = await CommandeService.updateCommandeState(
        selectedCommande._id,
        state
      );
      dispatch(updateCommande({ commande: data }));
      setSelectedCommande(data);
      dispatch(setGlobalIsLoading({ isLoading: false }));
      setStateMenuOpen(false);
    } catch (error) {
      dispatch(setGlobalIsLoading({ isLoading: false }));
      toast(`${error}`);
    }
  };
  const getState = (state) => {
    if (state === "draft") {
      return { state: "Brouillon", color: theme.palette.secondary[700] };
    } else if (state === "tovalidate") {
      return { state: "À Valider", color: theme.palette.warning[600] };
    } else if (state === "validated") {
      return { state: "Validé", color: theme.palette.success.main };
    } else if (state === "late") {
      return { state: "En retard", color: theme.palette.error[600] };
    } else {
      return { state: "", color: "" };
    }
  };
  const onClick = async () => {
    try {
      dispatch(setGlobalIsLoading({ isLoading: true }));
      await CommandeService.deleteCommande(selectedCommande._id);
      dispatch(deleteCommande({ id: selectedCommande._id }));
      dispatch(setGlobalIsLoading({ isLoading: false }));
      detailsOpen && setDetailsOpen(false);
    } catch (error) {
      dispatch(setGlobalIsLoading({ isLoading: false }));
      toast(`${error}`);
    }
  };

  return (
    <>
      {commandes && Array.isArray(commandes) && commandes.length !== 0 ? (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: "10px",
            alignItems: "end",
            p: "15px",
          }}
        >
          <TableContainer
            component={Paper}
            sx={{
              maxHeight: "440px",
              backgroundColor: theme.palette.primary.main,
              display: "flex",
            }}
          >
            <Table aria-label="simple table">
              <TableHead>
                <TableRow>
                  {tableHeader.map((header) => (
                    <TableCell
                      key={header}
                      sx={{
                        color: theme.palette.grey[600],
                        fontSize: "14px",
                        fontWeight: "400",
                        borderBottom: "2px solid",
                        borderBottomColor: theme.palette.grey.light,
                        pt: "5px",
                        pb: "5px",
                      }}
                    >
                      {header}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {commandes.map((commande) => (
                  <TableRow
                    key={commande._id}
                    sx={{
                      "&:last-child td, &:last-child th": { border: 0 },
                      "& .MuiTableCell-root": {
                        color: theme.palette.secondary.dark,
                        fontSize: "14px",
                        borderBottom: "1px solid",
                        borderBottomColor: theme.palette.grey.light,
                        pt: "5px",
                        pb: "5px",
                      },
                    }}
                  >
                    <TableCell component="th" scope="row">
                      <Typography
                        sx={{
                          textWrap: "nowrap",
                        }}
                      >
                        {commande.commande_number}
                      </Typography>
                    </TableCell>
                    <TableCell component="th" scope="row">
                      <Typography
                        sx={{
                          textWrap: "nowrap",
                        }}
                      >
                        {commande.client?.nom}
                      </Typography>
                    </TableCell>
                    <TableCell>{commande.ht_total}</TableCell>
                    <TableCell>{formatDate(commande.payment_date)}</TableCell>
                    <TableCell>
                      <Typography
                        sx={{
                          color: getState(commande.state).color,
                          fontSize: "14px",
                          textWrap: "nowrap",
                        }}
                      >
                        {getState(commande.state).state}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "end",
                          alignItems: "center",
                        }}
                      >
                        <IconButton
                          onClick={async () => {
                            setSelectedCommande(commande);
                            setDetailsOpen(true);
                            setTimeout(async () => {
                              await exportFile();
                              setDetailsOpen(false);
                            }, 1100);
                          }}
                        >
                          <SaveAltOutlined
                            sx={{
                              color: theme.palette.alt.main,
                              fontSize: "24px",
                            }}
                          />
                        </IconButton>
                        <IconButton onClick={() => navigate(`${commande._id}`)}>
                          <BorderColorOutlined
                            sx={{
                              color: theme.palette.alt.main,
                              fontSize: "24px",
                            }}
                          />
                        </IconButton>
                        <IconButton
                          onClick={() => {
                            setSelectedCommande(commande);
                            setDeleteCommandeOpen(true);
                          }}
                        >
                          <DeleteOutline
                            sx={{
                              color: theme.palette.alt.main,
                              fontSize: "24px",
                            }}
                          />
                        </IconButton>
                        <Button
                          sx={{
                            color: theme.palette.alt.main,
                            textTransform: "none",
                            fontSize: "12px",
                            fontWeight: "600",
                          }}
                          onClick={() => {
                            setSelectedCommande(commande);
                            setDetailsOpen(true);
                          }}
                        >
                          Détails
                        </Button>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <CustomDialog
            title={`Supprimer commande n°${selectedCommande.commande_number} ?`}
            content={`Vous êtes sûr de supprimer commande n°${selectedCommande.commande_number} ?`}
            onClick={onClick}
            open={deleteCommandeOpen}
            setOpen={setDeleteCommandeOpen}
          />
          <DetailsPopUp
            open={detailsOpen}
            setOpen={setDetailsOpen}
            descriptions={selectedCommande.descriptions}
            data={{
              name: selectedCommande.supplier_name,
              email: selectedCommande.supplier_email,
              phone: selectedCommande.supplier_phone,
              address: selectedCommande.supplier_address,
            }}
            values={selectedCommande}
            isSupplier={false}
            name={`Commande ${selectedCommande.commande_number ?? ""}`}
            client={selectedCommande.client ?? {}}
            actionButtons={
              <>
                <Box ref={stateButtonRef}>
                  <ActionButton
                    iconName="info"
                    text="Statut du Bon"
                    isEndIcon={true}
                    onClick={() => setStateMenuOpen(true)}
                    subText={getState(selectedCommande.state).state}
                  />
                  <Menu
                    anchorReference="anchorEl"
                    anchorOrigin={{
                      vertical: "center",
                      horizontal: "center",
                    }}
                    anchorEl={stateButtonRef.current}
                    open={stateMenuOpen}
                    onClose={() => setStateMenuOpen(false)}
                    MenuListProps={{
                      style: {
                        backgroundColor: theme.palette.primary.main,
                        color: theme.palette.grey[600],
                        border: "1px solid",
                        borderColor: theme.palette.primary.main,
                        borderRadius: "6px",
                      },
                    }}
                  >
                    {commandeStates.map((state) => (
                      <MenuItem
                        key={state}
                        sx={{
                          fontWeight: "bold",
                          fontSize: "12px",
                          ":hover": {
                            backgroundColor: theme.palette.grey[50],
                          },
                          backgroundColor:
                            selectedCommande?.state === state &&
                            theme.palette.grey[50],
                        }}
                        disabled={selectedCommande?.state === state}
                        onClick={() => updateState(state)}
                      >
                        {getState(state).state}
                      </MenuItem>
                    ))}
                  </Menu>
                </Box>
                <ActionButton
                  iconName="download"
                  text="Exporter en PDF"
                  onClick={exportFile}
                />
                <ActionButton
                  iconName="edit"
                  text="Modifier le bon"
                  onClick={() => {
                    setDetailsOpen(false);
                    navigate(selectedCommande._id);
                  }}
                />
                <ActionButton
                  iconName="remove"
                  text="Supprimer le bon"
                  onClick={() => setDeleteCommandeOpen(true)}
                />
              </>
            }
          />
        </Box>
      ) : (
        <NotFoundError text="Aucun Commande trouvé !" sx={{ height: "80%" }} />
      )}
    </>
  );
};

export default CommandeTable;
